/**
 * Detects the one Dexie failure the app cannot work around: an on-disk
 * database at a HIGHER version than this build declares. IndexedDB refuses to
 * open below the stored version, so `db.open()` rejects with a VersionError and
 * every later Dexie call fails too.
 *
 * This exists because `useSessionStore.boot()` swallows Dexie failures by
 * design (private mode, a corrupt store — neither of which should block boot).
 * That catch turns a version mismatch into a login screen where nothing ever
 * caches and nothing explains why, so it is detected separately here.
 *
 * The reset is deliberately NOT automatic. A deploy rollback produces exactly
 * this error against a database the PREVIOUS build can still read, and deleting
 * captured leads to recover from a rollback would turn an inconvenience into
 * data loss — the same reasoning as `clearCachedSession` in lib/db.ts.
 */
import { db } from '@/lib/db';

export type DbOpenFailure = 'version-mismatch';

export async function checkDatabaseOpens(): Promise<DbOpenFailure | null> {
  try {
    await db.open();
    return null;
  } catch (error: unknown) {
    if (error instanceof Error && error.name === 'VersionError') return 'version-mismatch';
    // Anything else (private mode, quota) is already handled by boot()'s catch
    // and must not take over the screen.
    return null;
  }
}

export async function resetLocalDatabase(): Promise<void> {
  await db.delete();
  window.location.reload();
}
